import classNames from 'classnames'
import Color from 'color'
import { startsWith } from 'lodash'
import { MdiReactIconComponentType } from 'mdi-react'
import ExpandLessIcon from 'mdi-react/ExpandLessIcon'
import ExpandMoreIcon from 'mdi-react/ExpandMoreIcon'
import React from 'react'
import { Link } from 'react-router-dom'

import { Collapse, List, ListItem, ListItemIcon, ListItemText, Theme } from '@mui/material'
import { createStyles, withStyles, WithStyles } from '@mui/styles'

class Navigation extends React.Component<NavigationProps & WithStyles<typeof styles>, NavigationState> {
  constructor(props: NavigationProps & WithStyles<typeof styles>) {
    super(props)
    const { elements, currentPath } = this.props
    const areChildrenInPath =
      elements &&
      elements.find(
        (element) =>
          !!element.children && !!element.children.find((nestedElement) => nestedElement.path === currentPath)
      )
    this.state = {
      open: Boolean(areChildrenInPath),
    }
  }

  handleClick = () => {
    this.setState((state) => ({ open: !state.open }))
  }

  render() {
    const { classes, currentPath, elements, nestingLevel = 0 } = this.props
    if (!elements) {
      return null
    }

    const nested = nestingLevel > 0
    const wrapperComponent = nested ? 'div' : 'nav'
    return (
      <List key={`list-${nestingLevel}`} component={wrapperComponent} disablePadding>
        {elements.map((element) => {
          const isLink = !!element.path
          const hasChildren = !!element.children

          const component = isLink ? getInnerLink(element.path!) : null

          const Icon = element.icon
          const isSelected = startsWith(currentPath, element.path)

          return (
            <React.Fragment key={`item-${element.path}`}>
              <ListItem
                button
                classes={{
                  button: classNames({
                    [classes.nestedListItem]: nested,
                    [classes.unselectedListItem]: !isSelected,
                    [classes.selectedListItem]: isSelected,
                  }),
                }}
                component={component as any}
                onClick={hasChildren ? this.handleClick : element.onClick}
              >
                <ListItemIcon>
                  <Icon
                    className={classNames({
                      [classes.unselectedIcon]: !isSelected,
                      [classes.selectedIcon]: isSelected,
                    })}
                  />
                </ListItemIcon>
                <ListItemText
                  className={classes.listItemText}
                  classes={{
                    primary: classNames({
                      [classes.unselectedListItemText]: !isSelected,
                      [classes.selectedListItemText]: isSelected,
                    }),
                  }}
                  primary={element.label}
                />
                {hasChildren ? this.state.open ? <ExpandLessIcon /> : <ExpandMoreIcon /> : null}
              </ListItem>
              <Collapse in={hasChildren ? this.state.open : true} timeout="auto" unmountOnExit>
                <Navigation
                  classes={classes}
                  currentPath={currentPath}
                  elements={element.children}
                  nestingLevel={nestingLevel + 1}
                />
              </Collapse>
            </React.Fragment>
          )
        })}
      </List>
    )
  }
}

const getInnerLink = (path: string) => {
  const linkComponent = React.forwardRef<Link>((linkProps, ref) => <Link to={path} {...linkProps} ref={ref as any} />)
  linkComponent.displayName = 'InnerLink'
  return linkComponent
}

type NavigationState = {
  open: boolean
}

export type NavigationProps = {
  opened?: boolean
  currentPath: string
  elements?: NavigationElement[]
  nestingLevel?: number
}

export type NavigationElement = {
  label: string
  icon: MdiReactIconComponentType
  path?: string
  children?: NavigationElement[]
  onClick?: () => any
}

const styles = ({ palette, spacing }: Theme) => {
  const themeHoverAlpha = Color(palette.action.hover).alpha()
  const contrastToPrimaryColor = Color(palette.primary.contrastText)
  const contrastHoverColor = contrastToPrimaryColor.alpha(themeHoverAlpha)
  const contrastHoverProperties = {
    '&:hover': {
      backgroundColor: contrastHoverColor.string(),
    },
  }

  const contrastRippleProperties = {
    color: palette.primary.contrastText,
  }

  const listItemProps = {
    iconColor: palette.primary.contrastText,
    ...contrastRippleProperties,
    ...contrastHoverProperties,
  }

  return createStyles({
    listItemText: {
      padding: 0,
    },
    unselectedIcon: {
      fill: palette.primary.contrastText,
    },
    unselectedListItem: {
      ...listItemProps,
    },
    unselectedListItemText: {
      color: palette.primary.contrastText,
    },
    selectedIcon: {
      fill: palette.primary.main,
    },
    nestedListItem: {
      paddingLeft: spacing(4),
      backgroundColor: Color(palette.primary.dark).darken(0.2).toString(),
    },
    selectedListItem: {
      backgroundColor: palette.primary.contrastText,
      '&:hover': {
        backgroundColor: Color(palette.primary.contrastText).alpha(0.8).string(),
      },
    },
    selectedListItemText: {
      color: palette.primary.main,
      fontWeight: 500,
    },
  })
}

export default withStyles(styles)(Navigation)
